<template>
  <div class="m-segmented-control">
    <LSegmentedControl
      :label="alias || ''"
      :segmented-control-items="segmentedItems"
      :model-value="stringModel"
      :disabled="isDisabled"
      :flexible="isFlexible"
      direction="row"
      @update:model-value="handleUpdate"
    >
      <template #l-label--label>
        <span class="m-segmented-control__label">
          {{ alias || '' }}
        </span>
      </template>
      <template v-if="appendIcon" #l-label--append-content>
        <LIconButton :icon="appendIcon" @click.stop="openModal" />
      </template>
      <slot />
    </LSegmentedControl>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGlobalStore } from '@/stores/globalStore.ts'
import { LIconButton, LSegmentedControl } from '@wgg.hyve/hyve-ui-library'

type Props = {
  items: Array<{ label: string; value: string | number }>
  modelValue: string | number | boolean
  alias?: string
  appendIcon?: string
  block?: boolean
  disabled?: boolean
  modalContent?: string
  readOnly?: boolean
  fullWidth?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
}>()

const { showModal } = useGlobalStore()
const { t } = useI18n()

const isDisabled = computed(() => !!props.disabled || !!props.readOnly)
const isFlexible = computed(() => !!props.block || !!props.fullWidth)

const segmentedItems = computed(
  () =>
    props.items?.map((item) => ({
      label: t(item.label),
      value: String(item.value)
    })) || []
)

const stringModel = computed(() => String(props.modelValue))

const handleUpdate = (value: unknown) => {
  const strValue = String(value)
  const original = props.items?.find((it) => String(it.value) === strValue)?.value
  emit('update:modelValue', (original ?? value) as string | number)
}

const openModal = () => {
  if (props.modalContent) {
    showModal(props.modalContent)
  }
}
</script>

<style lang="scss" scoped>
@import 'm-segmented-control';
</style>
